
import React from "react";
import { CreditCard, Beaker, AlertCircle } from "lucide-react";

interface SwapOfferDetailsProps {
  credit_offer: number | null;
  offered_reagents: {
    id: string;
    name: string;
    amount: number | null;
    amount_unit: string | null;
    catalogue_number?: string | null;
    description?: string | null;
    storage_conditions?: string | null;
  }[];
  offered_reagent?: {
    id: string;
    name: string;
    image_url: string | null;
  } | null;
  isPending: boolean;
}

const SwapOfferDetails: React.FC<SwapOfferDetailsProps> = ({ 
  credit_offer, 
  offered_reagents, 
  offered_reagent,
  isPending 
}) => {
  console.log("Rendering SwapOfferDetails:", {
    credit_offer,
    offered_reagents,
    offered_reagent,
    isPending
  });
  
  const hasReagentOffer = (offered_reagents && offered_reagents.length > 0) || offered_reagent;
  const hasCreditOffer = credit_offer && credit_offer > 0;
  
  // For compact view (past requests)
  if (!isPending) {
    if (hasCreditOffer) {
      return (
        <div className="flex items-center space-x-2 text-sm">
          <CreditCard className="h-3 w-3 text-muted-foreground" />
          <span>{credit_offer} credits offered</span>
        </div>
      );
    } else if (hasReagentOffer) {
      // Prioritize offered_reagent if available
      const reagentName = offered_reagent?.name || 
                         (offered_reagents?.length > 0 ? offered_reagents[0].name : "Reagent");
      
      return (
        <div className="flex items-center space-x-2 text-sm">
          <Beaker className="h-3 w-3 text-muted-foreground" />
          <span>{reagentName} offered</span>
        </div>
      );
    }
    
    // No specific offer
    return (
      <div className="flex items-center space-x-2 text-sm">
        <AlertCircle className="h-3 w-3 text-muted-foreground" />
        <span>No specific offer</span>
      </div>
    );
  }
  
  // For detailed view (pending requests)
  if (hasCreditOffer) {
    return (
      <div className="bg-secondary/30 p-3 rounded-md">
        <div className="flex items-center gap-2 font-medium">
          <CreditCard className="h-4 w-4 text-primary" />
          <span>Credit Offer: {credit_offer} credits</span>
        </div>
      </div>
    );
  } else if (hasReagentOffer) {
    // If we have direct offered_reagent reference (new format)
    if (offered_reagent) {
      return (
        <div className="bg-secondary/30 p-3 rounded-md">
          <div className="flex items-center gap-2 mb-1 font-medium">
            <Beaker className="h-4 w-4 text-primary" />
            <span>Reagent Offer</span>
          </div>
          <p className="font-medium">{offered_reagent.name}</p>
          <p className="text-sm text-muted-foreground mt-1">
            This reagent has been offered in exchange.
          </p>
        </div>
      );
    } 
    // If we have offered_reagents array data (old format)
    else if (offered_reagents && offered_reagents.length > 0) {
      const reagent = offered_reagents[0];
      
      return (
        <div className="bg-secondary/30 p-3 rounded-md">
          <div className="flex items-center gap-2 mb-1 font-medium">
            <Beaker className="h-4 w-4 text-primary" />
            <span>Reagent Offer</span>
          </div>
          <p className="font-medium">{reagent.name}</p>
          {reagent.amount && (
            <p className="text-sm">
              Amount: {reagent.amount} {reagent.amount_unit || ""}
            </p>
          )}
          {reagent.catalogue_number && (
            <p className="text-sm">
              Catalogue #: {reagent.catalogue_number}
            </p>
          )}
          {reagent.description && (
            <p className="text-sm mt-1 text-muted-foreground">
              {reagent.description.substring(0, 100)}
              {reagent.description.length > 100 ? "..." : ""}
            </p>
          )}
          {reagent.storage_conditions && (
            <p className="text-sm mt-1">
              Storage: {reagent.storage_conditions}
            </p>
          )}
        </div>
      );
    }
  }
  
  // No specific offer made (user just requested with a message but no reagent or credit)
  return (
    <div className="bg-secondary/30 p-3 rounded-md">
      <div className="flex items-center gap-2 mb-1">
        <AlertCircle className="h-4 w-4 text-amber-500" />
        <span className="font-medium">Open Request</span>
      </div>
      <p className="text-sm text-muted-foreground">
        The requester has not specified a reagent or credit offer. 
        This is an open request for your reagent.
      </p>
    </div>
  );
};

export default SwapOfferDetails;
